/**
 * HandoffNotesPage.jsx
 * CCH Care — Phase 5
 * Last Modified: 2026-06-04
 *
 * Shift-to-shift handoff notes:
 *  - Previous shift: read + individual/bulk acknowledge
 *  - Current shift:  compose, edit, delete
 *  - Priority levels: urgent / important / normal
 *  - General notes + resident-specific notes
 *
 * Changes (2026-06-04) — Outgoing-shift mode:
 * - Page now consumes useShift (outgoingShift / isOutgoingMode). When the
 *   user is in outgoing mode (closing out the shift behind them during a
 *   transition overlap), load() passes that shift's care_shift_id to the
 *   backend so the "Current" section binds to the OUTGOING shift — its
 *   existing notes display, and new notes attach to it for the incoming
 *   team. Previously the page only ever saw the facility's active shift,
 *   so notes silently landed on the incoming shift and the outgoing shift
 *   was unreachable.
 * - Compose/edit/delete needed no internal change: ComposeForm already
 *   binds care_shift_id to whatever shift lands in the Current section, so
 *   retargeting that shift on the backend is sufficient.
 * - Added an amber banner + retitled section ("Outgoing Shift — closing
 *   out") so a note can't silently attach to the wrong shift. Uses
 *   COLORS.warning* tokens to match the existing acknowledgment banner and
 *   stay dark-mode-correct.
 * - load() useCallback deps extended to [isOutgoingMode, outgoingShiftId];
 *   toggling mode anywhere (AppShell chip / dashboard banner) propagates
 *   through ShiftContext and refetches the now-effective shift here.
 *
 * Changes (2026-04-22):
 * - Fixed load() response shape. Endpoint returns {success, current_shift,
 *   previous_shift, residents} flat at the top level, matching the rest
 *   of the app's convention. Frontend was reading res.data.data (one
 *   level too deep), so data was always undefined, which made the
 *   "No active shift" empty-state render even when a current shift
 *   existed server-side. Now reads res.data directly.
 *
 * Dark mode (2026-03-26): added COLORS import; replaced all structural
 *   hardcoded hex values with COLORS.* tokens. Semantic priority colours
 *   (red/amber/slate) are preserved as-is — they convey meaning.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import useShift from '../../hooks/useShift';
import client from '../../api/client';
import { COLORS } from '../../config/constants';

import { formatTime, formatShortDate } from '../../utils/formatters';
const HN_BASE  = '/handoff/handoff_notes.php';
const ACK_BASE = '/handoff/handoff_acknowledgments.php';

// Action-based POST helper (matches codebase pattern)
async function apiPost(path, action, body) {
  const res = await client.post(`${path}?action=${action}`, body);
  return res.data;
}

// Semantic priority colours — intentional, not theme-responsive
const PRIORITY = {
  urgent:    { label: 'Urgent',    color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', dot: '🔴' },
  important: { label: 'Important', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', dot: '🟡' },
  normal:    { label: 'Normal',    color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', dot: '⚪' },
};

// ─── PriorityBadge ────────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  const cfg = PRIORITY[priority] || PRIORITY.normal;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4,
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      {cfg.dot} {cfg.label}
    </span>
  );
}

// ─── NoteCard ─────────────────────────────────────────────────────────────────

function NoteCard({ note, isCurrentShift, canManageAny, onEdit, onDelete, onAcknowledge }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const cfg   = PRIORITY[note.priority] || PRIORITY.normal;
  const time  = formatTime(note.created_at);
  const edited = note.updated_at && note.updated_at !== note.created_at;

  const canEdit   = note.is_mine || canManageAny;
  const canDelete = note.is_mine || canManageAny;

  return (
    <div style={{
      background: COLORS.surface,
      borderRadius: 10,
      border: `1px solid ${cfg.border}`,
      borderLeft: `4px solid ${cfg.color}`,
      padding: '12px 14px',
      marginBottom: 8,
    }}>
      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 9 }}>
        <PriorityBadge priority={note.priority} />

        {note.patient_name ? (
          <span style={{
            fontSize: 11, fontWeight: 600, color: COLORS.primary,
            background: COLORS.primaryLight, border: `1px solid ${COLORS.primary}44`,
            padding: '2px 8px', borderRadius: 12,
          }}>
            👤 {note.patient_name}{note.patient_room ? ` · Rm ${note.patient_room}` : ''}
          </span>
        ) : (
          <span style={{
            fontSize: 11, fontWeight: 600, color: COLORS.textMuted,
            background: COLORS.bg, border: `1px solid ${COLORS.border}`,
            padding: '2px 8px', borderRadius: 12,
          }}>
            🏢 General
          </span>
        )}

        <span style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.textLight }}>
          {time}{edited && ' · edited'}
        </span>
      </div>

      {/* ── Note text ── */}
      <p style={{
        margin: '0 0 10px', fontSize: 14, color: COLORS.text,
        lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {note.note_text}
      </p>

      {/* ── Footer row ── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 12, color: COLORS.textMuted }}>
          — {note.author_name}
          <span style={{ color: COLORS.textLight }}> ({note.author_role})</span>
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Previous-shift: acknowledge controls */}
          {!isCurrentShift && (
            note.is_acknowledged_by_me ? (
              <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600 }}>✓ Reviewed</span>
            ) : (
              <button
                onClick={() => onAcknowledge(note.handoff_note_id)}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6,
                  background: COLORS.primaryLight, color: COLORS.primary,
                  border: `1px solid ${COLORS.primary}44`, cursor: 'pointer',
                }}
              >
                ✓ Acknowledge
              </button>
            )
          )}

          {!isCurrentShift && note.acknowledgment_count > 0 && (
            <span style={{ fontSize: 11, color: COLORS.textLight }}>
              {note.acknowledgment_count} reviewed
            </span>
          )}

          {/* Current-shift: edit/delete */}
          {isCurrentShift && canEdit && !confirmDelete && (
            <button
              onClick={() => onEdit(note)}
              style={{
                fontSize: 11, padding: '4px 9px', borderRadius: 6,
                background: 'none', color: COLORS.textMuted,
                border: `1px solid ${COLORS.border}`, cursor: 'pointer',
              }}
            >
              Edit
            </button>
          )}
          {isCurrentShift && canDelete && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              style={{
                fontSize: 11, padding: '4px 9px', borderRadius: 6,
                background: 'none', color: COLORS.danger,
                border: `1px solid ${COLORS.dangerLight}`, cursor: 'pointer',
              }}
            >
              Delete
            </button>
          )}
          {confirmDelete && (
            <>
              <span style={{ fontSize: 11, color: COLORS.danger }}>Delete this note?</span>
              <button
                onClick={() => { onDelete(note.handoff_note_id); setConfirmDelete(false); }}
                style={{
                  fontSize: 11, padding: '4px 9px', borderRadius: 6,
                  background: COLORS.danger, color: '#fff', border: 'none', cursor: 'pointer',
                }}
              >
                Yes, delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                style={{
                  fontSize: 11, padding: '4px 9px', borderRadius: 6,
                  background: 'none', color: COLORS.textMuted,
                  border: `1px solid ${COLORS.border}`, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ComposeForm ──────────────────────────────────────────────────────────────

function ComposeForm({ shiftId, residents, editNote, onSave, onCancel }) {
  const [priority, setPriority]   = useState(editNote?.priority || 'normal');
  const [patientId, setPatientId] = useState(editNote?.patient_id ? String(editNote.patient_id) : '');
  const [noteText, setNoteText]   = useState(editNote?.note_text || '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  async function handleSubmit() {
    if (!noteText.trim()) { setError('Please enter a note before saving.'); return; }
    setSaving(true);
    setError('');
    try {
      if (editNote) {
        await apiPost(HN_BASE, 'update', {
          handoff_note_id: editNote.handoff_note_id,
          note_text: noteText.trim(),
          priority,
        });
      } else {
        await apiPost(HN_BASE, 'create', {
          care_shift_id: shiftId,
          note_text: noteText.trim(),
          priority,
          patient_id: patientId ? parseInt(patientId, 10) : null,
        });
      }
      onSave();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to save');
      setSaving(false);
    }
  }

  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'block', marginBottom: 5,
  };

  return (
    <div style={{
      background: COLORS.bg, border: `1px solid ${COLORS.border}`,
      borderRadius: 10, padding: 16, marginBottom: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 14 }}>
        {editNote ? '✏️ Edit Note' : '✏️ Add Handoff Note'}
      </div>

      {/* Priority */}
      <div style={{ marginBottom: 12 }}>
        <span style={labelStyle}>Priority</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {Object.entries(PRIORITY).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setPriority(key)}
              style={{
                flex: 1, padding: '7px 0', borderRadius: 7,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: priority === key ? cfg.bg : COLORS.surface,
                color:      priority === key ? cfg.color : COLORS.textMuted,
                border:     priority === key ? `1.5px solid ${cfg.color}` : `1px solid ${COLORS.border}`,
                transition: 'all 0.12s',
              }}
            >
              {cfg.dot} {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* Resident selector — only on new notes */}
      {!editNote && (
        <div style={{ marginBottom: 12 }}>
          <span style={labelStyle}>Resident (optional)</span>
          <select
            value={patientId}
            onChange={e => setPatientId(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 7, fontSize: 13,
              border: `1px solid ${COLORS.border}`, color: COLORS.text,
              background: COLORS.surface, boxSizing: 'border-box',
            }}
          >
            <option value="">General — not resident-specific</option>
            {residents.map(r => (
              <option key={r.patient_id} value={r.patient_id}>
                {r.first_name} {r.last_name}{r.room_number ? ` — Rm ${r.room_number}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Note text */}
      <div style={{ marginBottom: 12 }}>
        <span style={labelStyle}>Note</span>
        <textarea
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          placeholder="Describe the observation, situation, or instruction for the incoming shift…"
          rows={4}
          style={{
            width: '100%', padding: '8px 10px', borderRadius: 7, fontSize: 13,
            border: `1px solid ${COLORS.border}`, color: COLORS.text,
            background: COLORS.surface, lineHeight: 1.55,
            resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />
      </div>

      {error && (
        <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600,
            background: COLORS.surface, color: COLORS.textMuted,
            border: `1px solid ${COLORS.border}`, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            padding: '8px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600,
            background: COLORS.primary, color: '#fff', border: 'none', cursor: 'pointer',
            opacity: saving ? 0.65 : 1,
          }}
        >
          {saving ? 'Saving…' : editNote ? 'Save Changes' : 'Add Note'}
        </button>
      </div>
    </div>
  );
}

// ─── ShiftSection ─────────────────────────────────────────────────────────────

function ShiftSection({ title, shift, isCurrentShift, residents, canManageAny, onRefresh }) {
  const [composing, setComposing] = useState(false);
  const [editNote, setEditNote]   = useState(null);
  const [acking, setAcking]       = useState(false);

  const notes        = shift?.notes || [];
  const unackedCount = isCurrentShift ? 0 : notes.filter(n => !n.is_acknowledged_by_me).length;
  const allAcked     = !isCurrentShift && notes.length > 0 && unackedCount === 0;

  function shiftMeta() {
    if (!shift) return '';
    const start = formatTime(shift.start_time);
    const dateStr = formatShortDate(shift.start_time);
    const endStr  = shift.end_time
      ? formatTime(shift.end_time)
      : 'ongoing';
    return `${shift.shift_type || 'Shift'} · ${dateStr} · ${start} – ${endStr}`;
  }

  async function handleAcknowledge(noteId) {
    try {
      await client.post(ACK_BASE, { handoff_note_id: noteId });
      onRefresh();
    } catch (e) {
      console.error('Acknowledge failed:', e?.response?.data?.error || e.message);
    }
  }

  async function handleAcknowledgeAll() {
    const ids = notes.filter(n => !n.is_acknowledged_by_me).map(n => n.handoff_note_id);
    if (!ids.length) return;
    setAcking(true);
    try {
      await client.post(ACK_BASE, { handoff_note_ids: ids });
      onRefresh();
    } catch (e) {
      console.error('Acknowledge all failed:', e?.response?.data?.error || e.message);
    } finally {
      setAcking(false);
    }
  }

  async function handleDelete(noteId) {
    try {
      await apiPost(HN_BASE, 'delete', { handoff_note_id: noteId });
      onRefresh();
    } catch (e) {
      console.error('Delete failed:', e?.response?.data?.error || e.message);
    }
  }

  return (
    <div>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</div>
          {shift && (
            <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>{shiftMeta()}</div>
          )}
        </div>

        {isCurrentShift && shift && !composing && !editNote && (
          <button
            onClick={() => setComposing(true)}
            style={{
              padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
              background: COLORS.primary, color: '#fff', border: 'none', cursor: 'pointer',
            }}
          >
            + Add Note
          </button>
        )}
      </div>

      {/* Acknowledgment banner — previous shift only */}
      {!isCurrentShift && notes.length > 0 && (
        <div style={{
          background: allAcked ? COLORS.accentLight : COLORS.warningLight,
          border: `1px solid ${allAcked ? COLORS.accentAlpha : COLORS.warningAlpha}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{allAcked ? '✅' : '⚠️'}</span>
          <div style={{ flex: 1, fontSize: 13, color: allAcked ? COLORS.accent : COLORS.warning, fontWeight: 500 }}>
            {allAcked
              ? 'You have reviewed all handoff notes from this shift.'
              : `You have ${unackedCount} unreviewed note${unackedCount !== 1 ? 's' : ''} from this shift.`
            }
          </div>
          {!allAcked && (
            <button
              onClick={handleAcknowledgeAll}
              disabled={acking}
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 13px', borderRadius: 6,
                background: COLORS.primary, color: '#fff', border: 'none', cursor: 'pointer',
                whiteSpace: 'nowrap', opacity: acking ? 0.65 : 1, flexShrink: 0,
              }}
            >
              {acking ? 'Reviewing…' : 'Review All'}
            </button>
          )}
        </div>
      )}

      {/* Compose / edit form */}
      {(composing || editNote) && (
        <ComposeForm
          shiftId={shift?.care_shift_id}
          residents={residents}
          editNote={editNote || null}
          onSave={() => { setComposing(false); setEditNote(null); onRefresh(); }}
          onCancel={() => { setComposing(false); setEditNote(null); }}
        />
      )}

      {/* No active shift message */}
      {isCurrentShift && !shift && (
        <div style={{
          textAlign: 'center', padding: '28px 16px', color: COLORS.textLight, fontSize: 13,
          background: COLORS.bg, borderRadius: 10, border: `1px dashed ${COLORS.border}`,
        }}>
          No active shift. Start a shift from the dashboard to add handoff notes.
        </div>
      )}

      {/* Empty state */}
      {shift && notes.length === 0 && !composing && (
        <div style={{
          textAlign: 'center', padding: '28px 16px', color: COLORS.textLight, fontSize: 13,
          background: COLORS.bg, borderRadius: 10, border: `1px dashed ${COLORS.border}`,
        }}>
          {isCurrentShift
            ? 'No notes yet this shift — tap + Add Note to leave something for the incoming team.'
            : 'No handoff notes were left from the previous shift.'
          }
        </div>
      )}

      {/* Notes */}
      {notes.map(note => (
        <NoteCard
          key={note.handoff_note_id}
          note={note}
          isCurrentShift={isCurrentShift}
          canManageAny={canManageAny}
          onEdit={n => { setEditNote(n); setComposing(false); }}
          onDelete={handleDelete}
          onAcknowledge={handleAcknowledge}
        />
      ))}
    </div>
  );
}

// ─── HandoffNotesPage (main export) ──────────────────────────────────────────

export default function HandoffNotesPage() {
  const { memberships } = useAuth();
  const role         = memberships?.[0]?.role || '';
  const canManageAny = ['Care Supervisor', 'Care Admin'].includes(role);

  // Outgoing-shift mode — when the user is closing out the shift behind them
  // during a transition overlap, the page must bind to the OUTGOING shift,
  // not the facility's active (incoming) shift. care_shift_id is a primitive
  // dep so load() only recreates when the actual id changes.
  const { outgoingShift, isOutgoingMode } = useShift();
  const outgoingShiftId = outgoingShift?.care_shift_id ?? null;

  const [data,      setData]      = useState(null);
  const [residents, setResidents] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  const load = useCallback(async () => {
    try {
      const qs = (isOutgoingMode && outgoingShiftId)
        ? `&care_shift_id=${outgoingShiftId}`
        : '';
      const res = await client.get(`${HN_BASE}?action=load${qs}`);
      // Endpoint returns {success, current_shift, previous_shift, residents}
      // flat at the top level — res.data, not res.data.data. In outgoing
      // mode current_shift is the shift being closed out.
      setData(res.data);
      setResidents(res.data?.residents || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isOutgoingMode, outgoingShiftId]);

  useEffect(() => { load(); }, [load]);

  // ── Loading ──
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
      <div style={{ color: COLORS.textMuted, fontSize: 14 }}>Loading handoff notes…</div>
    </div>
  );

  // ── Error ──
  if (error) return (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
      <div style={{ color: COLORS.danger, fontSize: 14 }}>Failed to load handoff notes</div>
      <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{error}</div>
      <button
        onClick={load}
        style={{
          marginTop: 16, padding: '8px 16px', borderRadius: 7, fontSize: 13,
          background: COLORS.primary, color: '#fff', border: 'none', cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );

  const showingOutgoing = isOutgoingMode && !!data?.current_shift;

  return (
    <div style={{ padding: '16px 16px 80px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, margin: 0 }}>
          💬 Handoff Notes
        </h1>
        <p style={{ fontSize: 13, color: COLORS.textMuted, margin: '4px 0 0', lineHeight: 1.4 }}>
          Shift-to-shift communication — general notes and resident-specific observations
        </p>
      </div>

      {/* Previous shift */}
      <ShiftSection
        title="Previous Shift"
        shift={data?.previous_shift}
        isCurrentShift={false}
        residents={residents}
        canManageAny={canManageAny}
        onRefresh={load}
      />

      {/* Divider */}
      <div style={{ margin: '20px 0', borderTop: `2px solid ${COLORS.border}`, position: 'relative' }}>
        <span style={{
          position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
          background: COLORS.bg, padding: '0 10px', fontSize: 11, color: COLORS.textLight,
          fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          {showingOutgoing ? 'Outgoing' : 'Current'}
        </span>
      </div>

      {/* Outgoing-mode banner — makes it unmistakable which shift a new note
          attaches to, so a note can't silently land on the wrong shift. */}
      {showingOutgoing && (
        <div style={{
          background: COLORS.warningLight,
          border: `1px solid ${COLORS.warningAlpha}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true">🕒</span>
          <div style={{ fontSize: 13, color: COLORS.warning, fontWeight: 500, lineHeight: 1.45 }}>
            You&apos;re documenting the {data.current_shift.shift_type || 'shift'} shift you&apos;re
            closing out. Notes added here attach to that shift for the incoming team to review.
          </div>
        </div>
      )}

      {/* Current (or outgoing) shift */}
      <ShiftSection
        title={showingOutgoing ? 'Outgoing Shift — closing out' : 'Current Shift'}
        shift={data?.current_shift}
        isCurrentShift={true}
        residents={residents}
        canManageAny={canManageAny}
        onRefresh={load}
      />
    </div>
  );
}